import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { jsPDF } from 'jspdf';
import { legalRegisterApi } from '../services/api';
import { useToast } from '../hooks/useToast';
import { useSwal } from '../hooks/useSwal';
import { PageHeader } from '../components/PageHeader';
import { NimbusTable } from '../components/NimbusTable';
import { SortableTh } from '../components/SortableTh';
import { useSortableData } from '../hooks/useSortableData';
import { usePagination } from '../hooks/usePagination';
import { Pagination } from '../components/Pagination';
import { toneFromMap, type Tone } from '../constants/tone';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type LegalRequirement = {
  lawId: string;
  lawName: string;
  jurisdiction?: string;
  howItApplies?: string;
  contactPersonId?: string;
  contactPersonName?: string;
  reviewDate?: string;
  status?: string;
  notes?: string;
};



// ---------------------------------------------------------------------------
// Option lists
// ---------------------------------------------------------------------------

const JURISDICTIONS = [
  'EU', 'UK', 'Ireland', 'USA', 'Canada', 'Australia', 'International', 'Other',
];

const STATUSES = [
  'Compliant', 'Under Review', 'Non-Compliant', 'Not Assessed', 'Exempt',
];

// ---------------------------------------------------------------------------
// PDF export — card layout, no flex, inline html2canvas
// ---------------------------------------------------------------------------

const exportPDF = async (
  requirements: LegalRequirement[],
  stats: { total: number; compliant: number; nonCompliant: number; underReview: number; notAssessed: number },
  setExporting: (v: boolean) => void,
) => {
  setExporting(true);
  try {
    const e = (s?: string | null) =>
      (s || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    const genDate = new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
    const fileDate = new Date().toISOString().slice(0, 10);

    const fmtDate = (iso?: string) => {
      if (!iso) return '\u2014';
      const d = new Date(iso);
      return isNaN(d.getTime()) ? iso : d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
    };

    const statusColor = (s?: string) => {
      switch (s) {
        case 'Compliant':     return '#15803d';
        case 'Non-Compliant': return '#dc2626';
        case 'Under Review':  return '#0891b2';
        case 'Exempt':        return '#6b7280';
        default:              return '#d97706';
      }
    };
    const statusBg = (s?: string) => {
      switch (s) {
        case 'Compliant':     return '#dcfce7';
        case 'Non-Compliant': return '#fee2e2';
        case 'Under Review':  return '#cffafe';
        case 'Exempt':        return '#f3f4f6';
        default:              return '#fef3c7';
      }
    };

    const sc = (label: string, value: number, color: string) =>
      `<div style="display:inline-block;width:calc(20% - 8px);margin:0 4px 8px 0;padding:10px 14px;background:#f9fafb;border:1px solid #e5e7eb;border-radius:6px;vertical-align:top;text-align:center;">` +
      `<span style="font-size:20px;font-weight:700;color:${color};">${value}</span><br>` +
      `<span style="font-size:11px;color:#6b7280;">${label}</span></div>`;

    const field = (label: string, value: string) =>
      `<p style="margin:0 0 10px 0;">` +
      `<span style="font-size:10px;font-weight:700;color:#6b7280;text-transform:uppercase;letter-spacing:0.06em;">${label}</span><br>` +
      `<span style="font-size:13px;color:#111827;line-height:1.5;">${value || '\u2014'}</span></p>`;

    const fieldRow = (f1: string, f2: string) =>
      `<table style="width:100%;border-collapse:collapse;"><tr>` +
      `<td style="width:50%;padding-right:16px;vertical-align:top;">${f1}</td>` +
      `<td style="width:50%;vertical-align:top;">${f2}</td>` +
      `</tr></table>`;

    const cards = requirements.map(r => {
      const sc_clr = statusColor(r.status);
      const sc_bg  = statusBg(r.status);
      return `<div style="border:1px solid #e5e7eb;border-radius:8px;margin-bottom:16px;">` +
        `<table style="width:100%;border-collapse:collapse;background:#f9fafb;border-bottom:1px solid #e5e7eb;"><tr>` +
        `<td style="padding:12px 16px;vertical-align:middle;">` +
        `<span style="font-size:14px;font-weight:700;color:#111827;">${e(r.lawName)}</span>` +
        (r.jurisdiction ? `<span style="margin-left:8px;padding:2px 10px;border-radius:12px;font-size:11px;font-weight:600;background:#f3f4f6;color:#374151;">${e(r.jurisdiction)}</span>` : '') +
        `</td>` +
        `<td style="padding:12px 16px;vertical-align:middle;text-align:right;white-space:nowrap;">` +
        `<span style="padding:3px 12px;border-radius:12px;font-size:11px;font-weight:600;background:${sc_bg};color:${sc_clr};">${e(r.status || 'Not Assessed')}</span>` +
        `</td></tr></table>` +
        `<div style="padding:16px 20px;">` +
        fieldRow(field('Contact Person', e(r.contactPersonName) || '\u2014'), field('Review Date', fmtDate(r.reviewDate))) +
        (r.howItApplies ? field('How It Applies', e(r.howItApplies.substring(0, 400)) + (r.howItApplies.length > 400 ? '\u2026' : '')) : '') +
        (r.notes ? field('Notes', e(r.notes.substring(0, 300)) + (r.notes.length > 300 ? '\u2026' : '')) : '') +
        `</div></div>`;
    }).join('');

    const html =
      `<div style="font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Helvetica,Arial,sans-serif;background:#ffffff;color:#111827;width:860px;padding:40px;">` +
      `<div style="padding:32px;background:#fafafa;border:1px solid #e5e7eb;border-radius:8px;margin-bottom:32px;">` +
      `<h1 style="font-size:28px;font-weight:800;color:#111827;margin:0 0 12px 0;">Legal Register</h1>` +
      `<div style="margin-bottom:20px;">` +
      `<span style="padding:4px 12px;border-radius:20px;font-size:12px;font-weight:600;color:#5b21b6;background:#ede9fe;">ISO 27001:2022 \u2014 Clause 4.2 / Annex A</span>` +
      `<span style="font-size:13px;color:#6b7280;margin-left:12px;">Generated ${genDate}</span>` +
      `</div>` +
      sc('Total', stats.total, '#111827') +
      sc('Compliant', stats.compliant, '#15803d') +
      sc('Non-Compliant', stats.nonCompliant, '#dc2626') +
      sc('Under Review', stats.underReview, '#0891b2') +
      sc('Not Assessed', stats.notAssessed, '#6b7280') +
      `</div>` +
      cards +
      `<table style="width:100%;border-collapse:collapse;margin-top:32px;"><tr>` +
      `<td style="padding-top:16px;border-top:1px solid #e5e7eb;font-size:11px;color:#9ca3af;">Legal Register \u00b7 ISO/IEC 27001:2022</td>` +
      `<td style="padding-top:16px;border-top:1px solid #e5e7eb;text-align:right;font-size:11px;color:#9ca3af;">Generated ${genDate}</td>` +
      `</tr></table>` +
      `</div>`;

    const wrapper = document.createElement('div');
    wrapper.style.cssText = 'position:absolute;top:99999px;left:0;width:860px;';
    wrapper.innerHTML = html;
    document.body.appendChild(wrapper);
    void wrapper.getBoundingClientRect();
    const fullH = wrapper.scrollHeight;
    const { default: html2canvas } = await import('html2canvas');
    const canvas = await html2canvas(wrapper, { scale: 2, useCORS: true, logging: false, backgroundColor: '#ffffff', width: 860, height: fullH, windowWidth: 860, windowHeight: fullH });
    document.body.removeChild(wrapper);
    const margin = 10, printW = 190;
    const imgH = canvas.height / 2;
    const ratio = printW / (canvas.width / 2);
    const docH = imgH * ratio + margin * 2;
    const pdf = new jsPDF({ orientation: 'p', unit: 'mm', format: [210, Math.max(docH, 210.1)] });
    pdf.addImage(canvas.toDataURL('image/jpeg', 0.95), 'JPEG', margin, margin, printW, imgH * ratio);
    pdf.save(`legal-register-${fileDate}.pdf`);
  } catch (err) {
    console.error(err);
  } finally {
    setExporting(false);
  }
};


// ---------------------------------------------------------------------------
// Badge helpers
// ---------------------------------------------------------------------------

// Genuinely domain-specific (compliance status). Was returning a full "np-chip np-tone-x"
// class string - the only helper in the app shaped that way, unlike every sibling's bare
// tone suffix (np-tone-${tone}), which meant a caller here could never compose it with an
// extra class the way every other chip call site can. Fixed to match, no colour change.
const STATUS_TONE: Record<string, Tone> = {
  Compliant: 'success',
  'Under Review': 'info',
  'Non-Compliant': 'danger',
  Exempt: 'neutral',
};
const getStatusTone = (status?: string): Tone => toneFromMap(STATUS_TONE, status, 'warning');

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const LegalRegisterPage: React.FC = () => {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const toast = useToast();
  const swal = useSwal();
  const [exporting, setExporting] = useState(false);

  const { data: items = [], isLoading } = useQuery<LegalRequirement[]>({
    queryKey: ['legal-register'],
    queryFn: legalRegisterApi.getAll,
  });

  const [search, setSearch] = useState('');
  const [filterJurisdiction, setFilterJurisdiction] = useState('');
  const [filterStatus, setFilterStatus] = useState('');

  // ---------------------------------------------------------------- form helpers removed (now handled by LegalRequirementFormPage)

  // ---------------------------------------------------------------- mutations

  const deleteMutation = useMutation({
    mutationFn: (lawId: string) => legalRegisterApi.delete(lawId),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['legal-register'] });
      toast.success('Requirement deleted.');
    },
    onError: (e: Error) => toast.error(`Delete failed: ${e.message}`),
  });

  const handleDelete = async (item: LegalRequirement) => {
    const confirmed = await swal.confirmDelete(item.lawName || item.lawId);
    if (!confirmed) return;
    deleteMutation.mutate(item.lawId);
  };

  // ---------------------------------------------------------------- derived data

  const stats = useMemo(() => ({
    total: items.length,
    compliant: items.filter((i) => i.status === 'Compliant').length,
    nonCompliant: items.filter((i) => i.status === 'Non-Compliant').length,
    underReview: items.filter((i) => i.status === 'Under Review').length,
    notAssessed: items.filter((i) => !i.status || i.status === 'Not Assessed').length,
  }), [items]);

  const searched = useMemo(() => {
    const q = search.toLowerCase();
    return items.filter((i) =>
      (!q || [i.lawName, i.jurisdiction, i.contactPersonName, i.howItApplies]
        .some((v) => v?.toLowerCase().includes(q)))
      && (!filterJurisdiction || i.jurisdiction === filterJurisdiction)
      && (!filterStatus || i.status === filterStatus),
    );
  }, [items, search, filterJurisdiction, filterStatus]);

  const { sorted: filtered, field: sortField, dir: sortDir, toggle: toggleSort } = useSortableData<LegalRequirement, keyof LegalRequirement>(
    searched,
    (i, f) => String(i[f] ?? ''),
    'lawName',
  );
  const { pageItems, page, setPage, totalPages, pageSize, totalItems } = usePagination(filtered, 25);

  // ---------------------------------------------------------------- render

  const formatDate = (iso?: string) => {
    if (!iso) return null;
    const d = new Date(iso);
    if (isNaN(d.getTime())) return iso;
    return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  return (
    <div className="container-xxl flex-grow-1 container-p-y np-page">
      <PageHeader
        icon="ri-scales-3-line"
        title="Legal Register"
        colour="danger"
        actions={
          <div className="d-flex gap-2">
            <button className="np-btn np-btn-ghost np-btn-sm" onClick={() => exportPDF(filtered, stats, setExporting)} disabled={exporting}>
              {exporting ? <><span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />Generating…</> : <><i className="ri-file-pdf-line" />Export PDF</>}
            </button>
            <button className="np-btn np-btn-primary" onClick={() => navigate('/legal-register/new')}>
              <i className="ri-add-line"></i>Add Requirement
            </button>
          </div>
        }
      />

      <div className="np-card">
        {/* Card header: title + stats */}
        <div className="np-card-head flex-wrap">
          <div className="d-flex align-items-center gap-2 me-auto">
            <i className="ri-scales-3-line text-danger"></i>
            <h2>Legal &amp; Regulatory Requirements</h2>
            <span className="np-chip np-tone-neutral np-tabular">{stats.total}</span>
          </div>
          <div className="d-flex align-items-center gap-2 flex-wrap">
            {stats.compliant > 0 && (
              <span className="np-chip np-tone-success">
                <i className="ri-shield-check-line"></i>{stats.compliant} compliant
              </span>
            )}
            {stats.nonCompliant > 0 && (
              <span className="np-chip np-tone-danger">
                <i className="ri-error-warning-line"></i>{stats.nonCompliant} non-compliant
              </span>
            )}
            {stats.underReview > 0 && (
              <span className="np-chip np-tone-info">
                <i className="ri-loop-left-line"></i>{stats.underReview} under review
              </span>
            )}
            {stats.notAssessed > 0 && (
              <span className="np-chip np-tone-warning">
                <i className="ri-time-line"></i>{stats.notAssessed} not assessed
              </span>
            )}
          </div>
        </div>

        {/* Filters toolbar */}
        <div className="np-card-body border-bottom py-3">
          <div className="d-flex gap-2 flex-wrap align-items-center">
            <div className="np-search" style={{ maxWidth: 280 }}>
              <i className="ri-search-line"></i>
              <input
                type="text"
                placeholder="Search name, jurisdiction, role…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
              {search && (
                <button className="np-icon-btn" onClick={() => setSearch('')}>
                  <i className="ri-close-line"></i>
                </button>
              )}
            </div>
            <select
              className="np-select"
              style={{ maxWidth: 170 }}
              value={filterJurisdiction}
              onChange={(e) => setFilterJurisdiction(e.target.value)}
            >
              <option value="">All Jurisdictions</option>
              {JURISDICTIONS.map((j) => <option key={j}>{j}</option>)}
            </select>
            <select
              className="np-select"
              style={{ maxWidth: 160 }}
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
            >
              <option value="">All Statuses</option>
              {STATUSES.map((s) => <option key={s}>{s}</option>)}
            </select>
            {(search || filterJurisdiction || filterStatus) && (
              <button
                className="np-btn np-btn-sm np-btn-ghost"
                onClick={() => { setSearch(''); setFilterJurisdiction(''); setFilterStatus(''); }}
              >
                <i className="ri-filter-off-line"></i>Clear
              </button>
            )}
            <span className="np-sub ms-auto">
              {filtered.length} result{filtered.length !== 1 ? 's' : ''}
            </span>
          </div>
        </div>

        {/* Table */}
        <div className="np-card-body p-0">
          {isLoading ? (
            <div className="text-center py-5">
              <div className="spinner-border text-danger"></div>
              <p className="np-sub mt-2 mb-0">Loading requirements…</p>
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-5">
              <i className="ri-scales-3-line ri-3x d-block mb-2 opacity-25"></i>
              <p className="np-sub mb-0">
                {items.length === 0
                  ? 'No legal requirements yet — add one to get started.'
                  : 'No results match your filters.'}
              </p>
            </div>
          ) : (
            <NimbusTable thead={<tr>
                  {(
                    [
                      ['lawName',           'Law / Requirement'],
                      ['jurisdiction',      'Jurisdiction'],
                      ['howItApplies',      'How it Applies'],
                      ['contactPersonName', 'Contact Person'],
                      ['reviewDate',        'Review Date'],
                      ['status',            'Status'],
                    ] as [keyof LegalRequirement, string][]
                  ).map(([field, label]) => (
                    <SortableTh key={field} field={field} active={sortField === field} dir={sortDir} onSort={toggleSort}>
                      {label}
                    </SortableTh>
                  ))}
                  <th style={{ width: 80 }}></th>
                </tr>}>
                {pageItems.map((item) => (
                  <tr key={item.lawId}>
                    <td>
                      <div className="fw-semibold">{item.lawName}</div>
                      {item.notes && (
                        <div className="np-sub text-truncate" style={{ maxWidth: 220 }} title={item.notes}>
                          {item.notes}
                        </div>
                      )}
                    </td>
                    <td>
                      {item.jurisdiction
                        ? <span className="np-chip np-tone-neutral">{item.jurisdiction}</span>
                        : <span className="np-sub">—</span>}
                    </td>
                    <td>
                      <span
                        className="np-sub d-block text-truncate"
                        style={{ maxWidth: 240 }}
                        title={item.howItApplies}
                      >
                        {item.howItApplies || '—'}
                      </span>
                    </td>
                    <td>{item.contactPersonName || <span className="np-sub">—</span>}</td>
                    <td className="np-tabular">
                      {formatDate(item.reviewDate) ?? <span className="np-sub">—</span>}
                    </td>
                    <td>
                      <span className={`np-chip np-tone-${getStatusTone(item.status)}`}>
                        {item.status || 'Not Assessed'}
                      </span>
                    </td>
                    <td>
                      <div className="d-flex gap-1">
                        <button
                          className="np-icon-btn"
                          title="Edit"
                          onClick={() => navigate(`/legal-register/${item.lawId}/edit`)}
                        >
                          <i className="ri-pencil-line"></i>
                        </button>
                        <button
                          className="np-icon-btn np-icon-btn-danger"
                          title="Delete"
                          onClick={() => handleDelete(item)}
                        >
                          <i className="ri-delete-bin-line"></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
            </NimbusTable>
          )}
          {pageItems.length > 0 && (
            <Pagination page={page} totalPages={totalPages} pageSize={pageSize} totalItems={totalItems} onPageChange={setPage} />
          )}
        </div>
      </div>
    </div>
  );
};
